import * as React from "react";
import { Box, CPHrBreak, CPText } from "shared/components/base";
import CPModal from "shared/components/shared/CPModal/CPModal";

interface PowerBIConfigHelpModalProps {
  open: boolean;
  onClose: () => void;
}

const mono: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: 11,
  wordBreak: "break-all",
};

const rows: [string, boolean, string, string][] = [
  [
    "Workspace ID",
    true,
    "The Power BI workspace (group) ID.",
    "app.powerbi.com/groups/[Workspace ID]/reports/…",
  ],
  [
    "Report ID",
    true,
    "The unique report ID.",
    "…/reports/[Report ID]/ReportSection…",
  ],
  [
    "Tenant ID",
    false,
    "Azure AD tenant ID. Leave blank to use the app default.",
    "72f988bf-86f1-41af-91ab-2d7cd011db47",
  ],
  [
    "Dataset ID",
    false,
    "The semantic model ID. Required when RLS is enabled.",
    "cfafbeb1-8037-4d0c-896e-a46fb27ff229",
  ],
  [
    "Default Page Name",
    false,
    "Internal page name to load by default.",
    "…/ReportSection[suffix]  ->  e.g. ReportSection3a4b2c1d",
  ],
  [
    "Requires Effective Identity (RLS)",
    false,
    "Passes the user's agency as the RLS identity. Requires Dataset ID.",
    "—",
  ],
  [
    "RLS Role",
    false,
    "Power BI RLS role name to apply with the effective identity. Separate multiple roles with a semicolon.",
    "Agent Security",
  ],
];

const PowerBIConfigHelpModal: React.FC<PowerBIConfigHelpModalProps> = ({
  open,
  onClose,
}) => {
  return (
    <CPModal
      title="Power BI Configuration"
      open={open}
      onClose={onClose}
      width={780}
    >
      <Box flexDirection="column" spacing={12}>
        <CPText>
          Values are found in the Power BI Service URL when viewing the report.
        </CPText>

        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
        >
          <thead>
            <tr style={{ background: "#f0f4f8" }}>
              {["Field", "Required", "Description", "Where to find it"].map(
                (h) => (
                  <th
                    key={h}
                    style={{
                      padding: "6px 10px",
                      border: "1px solid #dee2e6",
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(([field, required, desc, where], i) => (
              <tr
                key={field}
                style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}
              >
                <td
                  style={{
                    padding: "6px 10px",
                    border: "1px solid #dee2e6",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                  }}
                >
                  {field}
                  {required && <span style={{ color: "#c00" }}> *</span>}
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    border: "1px solid #dee2e6",
                    textAlign: "center",
                  }}
                >
                  {required ? "Yes" : "No"}
                </td>
                <td
                  style={{ padding: "6px 10px", border: "1px solid #dee2e6" }}
                >
                  {desc}
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    border: "1px solid #dee2e6",
                    ...mono,
                  }}
                >
                  {where}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <CPHrBreak />

        <Box
          style={{
            background: "#fff8e1",
            border: "1px solid #ffe082",
            borderRadius: 4,
            padding: "8px 12px",
          }}
          flexDirection="column"
          spacing={4}
        >
          <CPText bold fontSize="13px">
            Requires Effective Identity (RLS)
          </CPText>
          <span style={{ color: "#555", fontSize: ".9rem" }}>
            When checked the report embed will pass the user's agency as the
            effective identity for Row-Level Security (RLS) filtering. Set the{" "}
            <strong>RLS Role</strong> field to the matching Power BI role name
            (e.g. <em>Agent Security</em>). Separate multiple roles with a
            semicolon.
          </span>
        </Box>
      </Box>
    </CPModal>
  );
};

export default PowerBIConfigHelpModal;
